import _ from 'lodash';
import JSEncrypt from 'jsencrypt';

import config from '../../config/config';
import moment from 'moment';

export const setIf = (object, path, value) => {
    if (!_.isNil(value)) {
        _.set(object, path, value);
    }
};

export const retailIframeVersion = '0.0.1';

export function updateTransactionBodyWithFormData(transBody, formFields) {
    const transactionBody = _.cloneDeep(transBody);

    _.forEach(formFields, (field, fieldName) => {
        if (!_.isNil(field.value) && field.value !== '') {
            if (fieldName === 'expirationDate') {
                const [expirationMonth, expirationYear] = _.split(field.value, '/');

                setIf(transactionBody, 'card.expirationMonth', expirationMonth);
                setIf(transactionBody, 'card.expirationYear', expirationYear);
            }

            if (field.requestParameterPath) {
                setIf(transactionBody, field.requestParameterPath, field.value);
            }
        }
    });

    return transactionBody;
}

export const findPossibleGatewayTypes = (merchantId, currency, merchants) => {

    const { paymentOptions = [] } = _.find(merchants, { merchantId: merchantId }) || {};

    const gatewayTypes = paymentOptions
        .filter((paymentOption) => paymentOption.currency === currency)
        .map((paymentOption) => paymentOption.gateways)
        .flat()
        .map((gateway) => _.get(gateway, 'type'));

    return _.uniq(gatewayTypes);
};

export function isMerchantConfiguredForTerminalNmi(merchant) {
    return _.some(_.get(merchant, 'paymentOptions'), (option) => {
        return _.get(option, 'gateways[0].name') === 'nmi' && _.startsWith(_.get(option, 'gateways[0].label'), '-sk:');
    });
}

export function isMerchantConfiguredForTerminalUsaepay(merchant) {
    return _.some(_.get(merchant, 'paymentOptions'), (option) => {
        return _.get(option, 'gateways[0].name') === 'usaepay';
    });
}

export const findPossiblePaymentMethods = (merchantId, merchants) => {
    const { paymentOptions = [] } = _.find(merchants, { merchantId: merchantId }) || {};

    const paymentMethods = paymentOptions.map((paymentOption) => paymentOption.paymentMethod);

    return _.uniq(paymentMethods);
};

export const encryptValueForTokenex = (value) => {
    const jsEncrypt = new JSEncrypt();
    jsEncrypt.setKey(config.tokenex.publicEncryptionKey);

    return jsEncrypt.encrypt(value);
};
export const isCardExpired = (card) => {
    const expirationDate = `${_.padStart(card.expirationYear, 4, '20')}-${card.expirationMonth}`;

    return moment().isAfter(expirationDate, 'month');
};

export function shouldIncludePaymentFailureReason() {
    try {
        return typeof location !== 'undefined' && _.startsWith(location.pathname, '/dashboard');
    } catch {
        return false;
    }
}

export function getFailureMessageFromApiResponse(response, fallbackMessage = 'Payment failed', options = {}) {
    const includeReason = _.get(options, 'includeReason', shouldIncludePaymentFailureReason());

    // Root (`/`) should match historical behavior: do not surface specific failure reasons.
    if (!includeReason) {
        return fallbackMessage;
    }

    if (_.isString(response) && response.trim().length > 0) {
        return response.trim();
    }

    const payload = _.get(response, 'body') ?? response;

    const message = _.get(payload, 'message');
    if (_.isString(message) && message.trim().length > 0) {
        return message.trim();
    }

    const firstError = _.get(payload, 'errors[0].message') ?? _.get(payload, 'errors[0]');
    if (_.isString(firstError) && firstError.trim().length > 0) {
        return firstError.trim();
    }

    // Some flows return a "failed" terminal status without a message.
    if (_.get(payload, 'terminalRequestStatus') === 'failed') {
        return fallbackMessage;
    }

    return fallbackMessage;
}

export function isPaymentSuccessfulFromApiResponse(response) {
    if (!response) {
        return true;
    }

    // fetchMiddleware throws `{ statusCode, body }` on non-2xx responses.
    if (_.get(response, 'statusCode')) {
        return false;
    }

    const explicitFlags = [_.get(response, 'success'), _.get(response, 'approved'), _.get(response, 'isSuccess')];
    if (_.some(explicitFlags, (flag) => flag === false)) {
        return false;
    }

    const resultLike = [
        _.get(response, 'gatewayResponse.result'),
        _.get(response, 'gatewayResponse.status'),
        _.get(response, 'result'),
        _.get(response, 'status'),
        _.get(response, 'transactionStatus'),
        _.get(response, 'terminalRequestStatus')
    ].find(_.isString);

    if (resultLike && /(error|fail|failed|declin|declined|deny|denied|reject|rejected)/i.test(resultLike)) {
        return false;
    }

    // Any structured errors are treated as failures even if HTTP was 200.
    if (!_.isEmpty(_.get(response, 'errors')) || !_.isEmpty(_.get(response, 'error')) || !_.isEmpty(_.get(response, 'body.errors')) || !_.isEmpty(_.get(response, 'body.error'))) {
        return false;
    }

    return true;
}
